import { z } from 'zod';
import { ALLOCATION_KEYS, APPORTIONABLE, COST_CATEGORIES, DOCUMENT_CHANNELS, DOCUMENT_KINDS, METER_TYPES, METER_UNITS, OVERRIDE_LEVELS } from '../enums';
import { zCents, zDate, zId, zReason } from './common';

export const ALLOWED_UPLOAD_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/webp', 'text/csv', 'application/xml', 'text/xml'] as const;
export const CreateDocumentBody = z.object({
  propertyId: zId.optional(),
  kind: z.enum(DOCUMENT_KINDS).default('invoice'),
  channel: z.enum(DOCUMENT_CHANNELS).default('web'),
  mime: z.enum(ALLOWED_UPLOAD_MIME),
  size: z.number().int().positive().max(25 * 1024 * 1024, { message: 'Datei größer als 25 MB' }),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  originalName: z.string().max(200).default(''),
  pages: z.number().int().min(1).max(200).default(1),
  /**
   * What the person uploads this receipt for (Umlage-Assistent "Rechnung hochladen" for a cost type and year): the
   * review form starts with it where the AI found nothing. Never overrides recognised values.
   */
  uploadHint: z.object({
    category: z.enum(COST_CATEGORIES).optional(),
    serviceFrom: zDate.optional(),
    serviceTo: zDate.optional(),
    /** Heizkosten page: the metering statement is read there (own AI step with review), not by the receipt AI */
    heatingImport: z.boolean().optional(),
  })
    .refine((v) => !v.serviceFrom || !v.serviceTo || v.serviceFrom <= v.serviceTo, { message: 'Leistungszeitraum: Beginn nach Ende', path: ['serviceTo'] })
    .optional(),
});
export const DocumentFieldsBody = z.object({ fields: z.record(z.string(), z.unknown()) });
export const zScope = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('property') }),
  z.object({ kind: z.literal('group'), groupId: z.string() }),
  z.object({ kind: z.literal('units'), unitIds: z.array(zId).min(1) }),
]);
const costItemFields = {
  category: z.enum(COST_CATEGORIES),
  label: z.string().max(120).default(''),
  amountCents: zCents,
  vatCents: zCents.optional(),
  serviceFrom: zDate,
  serviceTo: zDate,
  scope: zScope.default({ kind: 'property' }),
  allocationKey: z.enum(ALLOCATION_KEYS).optional(),
  differenceKey: z.enum(ALLOCATION_KEYS).optional(),
  apportionable: z.enum(APPORTIONABLE).default('yes'),
  nonApportionableCents: zCents.min(0).optional(),
  laborCents35a: zCents.min(0).optional(),
  meterConsumption: z.object({ quantity: z.number().min(0), unit: z.string().max(10).optional() }).optional(),
  heatingSplit: z.object({ consumptionPct: z.number().min(50).max(70) }).optional(),
  /** Parts of one invoice split by outdoor areas (ADR-0010) share this id; informational, the engine ignores it. */
  splitGroupId: z.string().trim().min(1).max(40).optional(),
  /** Own type of "sonstige Betriebskosten" (property.customCostTypes, docs/15 #36): only with category other_operating. */
  customTypeId: z.string().trim().min(1).max(40).optional(),
};
/** Partly apportionable needs the non-apportionable part (> 0, below the amount): otherwise the engine passes on everything. */
export const partialOk = (v: { apportionable?: string; amountCents: number; nonApportionableCents?: number }) =>
  v.apportionable !== 'partial' || (!!v.nonApportionableCents && v.nonApportionableCents < v.amountCents);
const PARTIAL_MSG = { message: 'Teilweise umlagefähig: nicht umlagefähigen Teil angeben (mehr als 0, weniger als der Betrag)', path: ['nonApportionableCents'] };
export const ConfirmDocumentBody = z.object({
  propertyId: zId,
  vendor: z.string().max(200).optional(),
  invoiceNo: z.string().max(100).optional(),
  supersedesDocumentId: zId.optional(),
  /** Zuordnungsprüfung: the person confirms this property although the receipt names another one (or none of theirs). */
  placementConfirmed: z.boolean().optional(),
  items: z.array(z.object(costItemFields).refine((v) => v.serviceFrom <= v.serviceTo, { message: 'Leistungszeitraum: Beginn nach Ende', path: ['serviceTo'] }).refine(partialOk, PARTIAL_MSG)).min(1),
});
/** Zuordnungsprüfung (docs/06): move an unconfirmed receipt to the property its content names, or keep it here. */
export const DocumentPlacementBody = z.discriminatedUnion('action', [z.object({ action: z.literal('move'), propertyId: zId }), z.object({ action: z.literal('keep') })]);
export const CreateCostItemBody = z.object({ propertyId: zId, ...costItemFields, origin: z.enum(['manual', 'import']).default('manual'), manualReason: z.string().trim().min(3).max(300).optional(), /** recurring cost taken over from last year (Dauerbescheid, Versicherung, Wartungsvertrag): the item it repeats */ carriedOverFrom: zId.optional() })
  .refine((v) => v.serviceFrom <= v.serviceTo, { message: 'Leistungszeitraum: Beginn nach Ende', path: ['serviceTo'] })
  .refine(partialOk, PARTIAL_MSG)
  .refine((v) => v.origin !== 'manual' || !!v.manualReason, { message: 'Manuelle Positionen brauchen eine Begründung.', path: ['manualReason'] });
/**
 * One invoice split into several cost items (Außenflächen-Aufteilung, ADR-0010): saved together or not at all, all
 * parts share `splitGroupId`. The client computes the parts with the shared split functions; the server only checks
 * that they are complete (2–20 parts, same category and period).
 */
const HEAT_PAIR: readonly string[] = ['heating', 'hot_water'];
export const CreateCostItemSplitBody = z.object({
  propertyId: zId,
  origin: z.enum(['manual', 'import']).default('manual'),
  manualReason: z.string().trim().min(3).max(300).optional(),
  items: z.array(z.object(costItemFields).refine((v) => v.serviceFrom <= v.serviceTo, { message: 'Leistungszeitraum: Beginn nach Ende', path: ['serviceTo'] }).refine(partialOk, PARTIAL_MSG)).min(2).max(20),
})
  .refine((v) => v.origin !== 'manual' || !!v.manualReason, { message: 'Manuelle Positionen brauchen eine Begründung.', path: ['manualReason'] })
    // § 9 HeizkostenV: one fuel bill may be split into heating and hot water (ADR-0019), otherwise one cost type
  .refine((v) => v.items.every((i) => (i.category === v.items[0]!.category || (HEAT_PAIR.includes(i.category) && HEAT_PAIR.includes(v.items[0]!.category))) && i.serviceFrom === v.items[0]!.serviceFrom && i.serviceTo === v.items[0]!.serviceTo), { message: 'Alle Teile einer Rechnung: gleiche Kostenart (oder Heizung und Warmwasser) und gleicher Zeitraum', path: ['items'] });
export const UpdateCostItemBody = z.object({
  category: z.enum(COST_CATEGORIES).optional(),
  label: z.string().max(120).optional(),
  amountCents: zCents.optional(),
  serviceFrom: zDate.optional(),
  serviceTo: zDate.optional(),
  scope: zScope.optional(),
  allocationKey: z.enum(ALLOCATION_KEYS).nullable().optional(),
  differenceKey: z.enum(ALLOCATION_KEYS).nullable().optional(),
  apportionable: z.enum(APPORTIONABLE).optional(),
  nonApportionableCents: zCents.min(0).nullable().optional(),
  laborCents35a: zCents.min(0).nullable().optional(),
  meterConsumption: z.object({ quantity: z.number().min(0), unit: z.string().max(10).optional() }).nullable().optional(),
  heatingSplit: z.object({ consumptionPct: z.number().min(50).max(70) }).nullable().optional(),
  customTypeId: z.string().trim().min(1).max(40).nullable().optional(),
  active: z.boolean().optional(),
  manualReason: z.string().trim().min(3).max(300).optional(),
});
export const OverrideBody = z.object({ level: z.enum(OVERRIDE_LEVELS), targetId: z.string().optional(), value: z.number(), reason: zReason, billingPeriodId: zId.optional() })
  .refine((v) => v.level === 'cost_total' || !!v.targetId, { message: 'Empfänger fehlt', path: ['targetId'] })
  .refine((v) => v.level === 'key_value' || Number.isInteger(v.value), { message: 'Betrag in ganzen Cent', path: ['value'] });

export const CreateMeterBody = z.object({ propertyId: zId, unitId: zId.nullable().default(null), type: z.enum(METER_TYPES), unit: z.enum(METER_UNITS), serialNo: z.string().trim().min(1).max(60), installedOn: zDate, initialValue: z.number().min(0).default(0), calibrationDue: zDate.optional(), remoteReadable: z.boolean().default(false), label: z.string().max(60).optional() });
/**
 * Edit a meter. Type, unit and assignment (`unitId`: a unit of the property, null = Hauptzähler/Allgemein) change the
 * consumption basis: only while no finalised statement used the meter (server checks).
 */
export const UpdateMeterBody = z.object({
  unitId: zId.nullable().optional(),
  serialNo: z.string().trim().min(1).max(60).optional(),
  label: z.string().trim().max(60).nullable().optional(),
  calibrationDue: zDate.nullable().optional(),
  remoteReadable: z.boolean().optional(),
  type: z.enum(METER_TYPES).optional(),
  unit: z.enum(METER_UNITS).optional(),
}).refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Keine Änderung angegeben' });
export const ReplaceMeterBody = z.object({ date: zDate, oldFinalValue: z.number().min(0), serialNo: z.string().trim().min(1).max(60), newStartValue: z.number().min(0).default(0), calibrationDue: zDate.optional(), reason: z.string().max(300).optional() });
/** `estimated`: a value the landlord estimated (e.g. no access to the flat, § 9a HeizkostenV); engine interpolation is never stored. */
export const CreateReadingBody = z.object({ meterId: zId, date: zDate, value: z.number().min(0), photoFileId: zId.optional(), aiValue: z.number().optional(), note: z.string().max(500).optional(), estimated: z.boolean().optional() });
/** Correct a reading (not behind a final statement; server checks). The reason is kept in the reading's correction trail. */
export const UpdateReadingBody = z
  .object({ date: zDate.optional(), value: z.number().min(0).optional(), note: z.string().trim().max(500).nullable().optional(), estimated: z.boolean().optional(), reason: zReason })
  .refine((v) => v.date !== undefined || v.value !== undefined || v.note !== undefined || v.estimated !== undefined, { message: 'Keine Änderung angegeben' });
export const DeleteReadingBody = z.object({ reason: zReason });
/** Discard a wrongly uploaded receipt (soft delete, reason in the audit log). */
export const DiscardDocumentBody = z.object({ reason: zReason });
export const ReadingsQuery = z.object({
  meterId: z.string().optional(),
  propertyId: z.string().optional(),
  unitId: z.string().optional(),
  status: z.string().optional(),
  source: z.string().optional(),
  from: zDate.optional(),
  to: zDate.optional(),
  /** `details=true` adds who entered/reviewed it, photo URL and the final-statement lock (Zählerstand-Verlauf). */
  details: z.enum(['true', 'false']).optional(),
});
export const RequestReadingsBody = z.object({ propertyId: zId, dueDate: zDate, tenancyIds: z.array(zId).optional(), message: z.string().max(500).optional() });
